import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Dropdown } from "antd";
import { useWakeLock } from 'react-screen-wake-lock';
import { EyeInvisibleOutlined, EyeOutlined, FontSizeOutlined, FullscreenExitOutlined, FullscreenOutlined, MoonOutlined, SunOutlined, SoundOutlined, Loading3QuartersOutlined, MutedOutlined, } from "@ant-design/icons";
import { useI18n } from "components/I18nProvider";
import { fontSizes, getDefaultBgColor, getDefaultTextColor, isFontSize } from "../subtitles-settings";
import LanguageSelect from "components/LanguageSelect";
import TtsPopover from "./TtsPopover";
import styles from './style.module.scss';
const searchParams = new URLSearchParams(window.location.search);
const initialPopoverOpen = searchParams.get('accentTts') === 'on';
const SubtitlesFooter = ({ settings, language, languageLoading = false, onSettingsChange, onLanguageChange, segment, onAudioToggle, ttsStatus, visible, }) => {
    const { intl } = useI18n();
    const { isSupported: isWakeLockSupported, released, request, release } = useWakeLock({
        reacquireOnPageVisible: true,
    });
    const [voicePopoverOpen, setVoicePopoverOpen] = useState(initialPopoverOpen);
    const [popoverAllowed, setPopoverAllowed] = useState(visible);
    useEffect(() => {
        if (!visible) {
            setPopoverAllowed(false);
        }
        else {
            const timeout = setTimeout(() => setPopoverAllowed(true), 1000);
            return () => clearTimeout(timeout);
        }
    }, [visible]);
    const handleFontSizeChange = (item) => {
        onSettingsChange({ fontSize: isFontSize(item.key) ? item.key : 'lg' });
    };
    const handleSwitchWakeLock = () => {
        if (released === true || released === undefined) {
            request();
        }
        else {
            release();
        }
    };
    const handleSwitchTheme = () => {
        const newTheme = settings.theme === 'dark' ? 'light' : 'dark';
        onSettingsChange({
            theme: newTheme,
            textColor: getDefaultTextColor(newTheme),
            bgColor: getDefaultBgColor(newTheme)
        });
    };
    const handleSwitchFullscreen = () => {
        onSettingsChange({ fullscreen: !settings.fullscreen });
    };
    const items = Object.keys(fontSizes).map((key) => ({
        key,
        label: _jsx("span", { "test-id": `subtitles-font-size-${key}`, children: intl.formatMessage({ id: `${key}FontSize` }) }),
    }));
    return (_jsxs("div", { className: styles.subtitlesFooter, children: [_jsx("div", { className: styles.toolbar, children: _jsx(LanguageSelect, { testId: "subtitles-language-select", language: language, loading: languageLoading, onLanguageChange: onLanguageChange, segment: segment }) }), _jsxs("div", { className: styles.toolbar, children: [_jsx(Dropdown, { overlayClassName: styles.fontSize, menu: {
                            items,
                            selectable: true,
                            selectedKeys: [settings.fontSize ?? 'md'],
                            onSelect: handleFontSizeChange,
                        }, getPopupContainer: (triggerNode) => triggerNode.parentElement, trigger: ["click"], children: _jsx("button", { className: styles.toolbarButton, "test-id": "subtitles-font-size-button", children: _jsx(FontSizeOutlined, {}) }) }), _jsx(TtsPopover, { open: voicePopoverOpen && popoverAllowed, onOpenChange: setVoicePopoverOpen, ttsStatus: ttsStatus, onAudioToggle: onAudioToggle, language: language, children: _jsxs("button", { "test-id": "subtitles-tts-button", disabled: ttsStatus === 'n/a' || ttsStatus === 'stopped', className: `${styles.toolbarButton} ${styles.ttsButton} ${ttsStatus === 'on' ? styles.ttsOn : ''}`, children: [ttsStatus === 'on' && (_jsxs("span", { className: styles.soundWave, children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })), ttsStatus === 'off' && _jsx(SoundOutlined, {}), (ttsStatus === 'n/a' || ttsStatus === 'stopped') && _jsx(MutedOutlined, {}), ttsStatus === 'waiting' && _jsx(Loading3QuartersOutlined, { spin: true })] }) }), isWakeLockSupported && (_jsx("button", { "test-id": "subtitles-wake-lock-button", className: styles.toolbarButton, onClick: handleSwitchWakeLock, children: released === true || released === undefined ? _jsx(EyeInvisibleOutlined, {}) : _jsx(EyeOutlined, {}) })), _jsx("button", { "test-id": "subtitles-theme-button", className: styles.toolbarButton, onClick: handleSwitchTheme, children: settings.theme === 'dark' ? _jsx(SunOutlined, {}) : _jsx(MoonOutlined, {}) }), _jsx("button", { "test-id": "subtitles-fullscreen-button", className: styles.toolbarButton, onClick: handleSwitchFullscreen, children: settings.fullscreen ? _jsx(FullscreenExitOutlined, {}) : _jsx(FullscreenOutlined, {}) })] })] }));
};
export default SubtitlesFooter;
